import { ArrowRight, Sparkles } from "lucide-react";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, getReport, startReport } from "../api/client";

/** Real phase durations for a live audit, which takes about a minute end to end. */
const PHASES = [
  { label: "Selecting representative pages", holdMs: 22_000 },
  { label: "Extracting entities and meaning", holdMs: 12_000 },
  { label: "Mapping actions and interfaces", holdMs: 12_000 },
  { label: "Checking agent readiness", holdMs: Number.POSITIVE_INFINITY },
];

/**
 * Public sites verified to complete on the live deployment, one per archetype; the sample hosts
 * only resolve in demo mode. alpina.travel is the only WordLift client that may appear here.
 */
const LIVE_SITES = ["alpina.travel", "allbirds.com", "wordlift.io"];
const DEMO_SITES = ["alpina.travel", "shop.example", "publisher.example", "insurance.example", "saas.example", "organization.example"];

async function waitUntilVisible(reportId: string): Promise<void> {
  for (let attempt = 0; attempt < 20; attempt += 1) {
    try {
      await getReport(reportId);
      return;
    } catch (caught) {
      if (!(caught instanceof ApiError) || caught.status !== 404) throw caught;
      await new Promise((resolve) => window.setTimeout(resolve, 600));
    }
  }
}

export function HomeRoute() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [phaseIndex, setPhaseIndex] = useState<number | null>(null);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  // The live sites show at once; only a demo deployment swaps them, once its health check says so.
  const [mode, setMode] = useState<"demo" | "live">("live");
  const timers = useRef<number[]>([]);
  const tickers = useRef<number[]>([]);
  const phase = phaseIndex === null ? null : PHASES[phaseIndex].label;

  const stopClocks = () => {
    timers.current.forEach((timer) => window.clearTimeout(timer));
    tickers.current.forEach((ticker) => window.clearInterval(ticker));
    timers.current = [];
    tickers.current = [];
  };

  useEffect(() => stopClocks, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch("/api/health");
        const health = (await response.json()) as { mode?: string };
        if (!cancelled) setMode(health.mode === "demo" ? "demo" : "live");
      } catch {
        if (!cancelled) setMode("live");
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  function startClocks() {
    let elapsed = 0;
    timers.current = PHASES.slice(0, -1).map((entry, index) => {
      elapsed += entry.holdMs;
      return window.setTimeout(() => setPhaseIndex(index + 1), elapsed);
    });
    tickers.current = [
      window.setInterval(() => setSeconds((value) => value + 1), 1_000),
    ];
  }

  async function run(target: string) {
    setUrl(target);
    setError(null);
    setPhaseIndex(0);
    setSeconds(0);
    startClocks();
    try {
      const { reportId, ready } = startReport(target);
      // Leave for the report page as soon as the running record exists — it renders live
      // progress from there. A request refused outright still surfaces here.
      await Promise.race([ready, waitUntilVisible(reportId)]);
      ready.catch(() => undefined);
      navigate(`/reports/${reportId}`, { state: { started: true } });
    } catch (caught) {
      setPhaseIndex(null);
      setError(caught instanceof Error ? caught.message : "The audit could not be completed");
    } finally {
      stopClocks();
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void run(url);
  }

  const suggested = mode === "demo" ? DEMO_SITES : LIVE_SITES;

  return (
    <section className="home-page">
      <div className="hero" aria-labelledby="hero-title">
        <div className="eyebrow"><Sparkles size={16} /> Free · no account · public websites</div>
        <h1 id="hero-title">See how AI understands your business, <span>and what agents can do</span></h1>
        <p className="hero-copy">
          Paste a URL. WordLift builds a first model of your business from its pages, shows what the site declares and what it
          only says in its content, and checks what AI agents can actually do with it.
        </p>
        <form className="audit-form" onSubmit={submit}>
          <label htmlFor="site-url">Website URL</label>
          <div className="input-row">
            {/* Text, not type="url": the browser's own check turns "yourbusiness.com" away for want of https://,
                which the server adds. inputMode still brings up the address keyboard on a phone. */}
            <input
              id="site-url"
              name="url"
              type="text"
              inputMode="url"
              autoComplete="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={url}
              placeholder="yourbusiness.com"
              onChange={(event) => setUrl(event.target.value)}
              required
            />
            <button type="submit" disabled={Boolean(phase)}>
              {phase ? "Reading your site" : "Audit my site"} <ArrowRight size={18} />
            </button>
          </div>
          {(
            <div className="try-sites" aria-label="Suggested sites">
              <span className="try-sites-label">
                {mode === "demo" ? "Demo mode — pick a sample site:" : "No site handy? Try one of these:"}
              </span>
              {suggested.map((host) => (
                <button
                  key={host}
                  type="button"
                  className="try-site"
                  disabled={Boolean(phase)}
                  onClick={() => void run(`https://${host}`)}
                >
                  {host} <ArrowRight size={13} aria-hidden="true" />
                </button>
              ))}
            </div>
          )}
          {phase && (
            <div className="progress-message">
              <span className="progress-dot" aria-hidden="true" />
              <span className="progress-detail" role="status">{phase}</span>
              <span className="progress-detail" aria-hidden="true">{seconds}s</span>
            </div>
          )}
          {error && <p className="form-error" role="alert">{error}</p>}
        </form>
      </div>
    </section>
  );
}
